<template>
    <div>
        <div class="minmax-inputs">
            <input class="form-control" type="number" :step="step" v-model="sliderMinValue" :disabled="min === null || max === null"/>
            <div class="slider-wrapper">
                <div ref="slider" class="custom-slider minmax">
                    <div class="minmax-indicator"></div>
                    <input
                        ref="inputMin"
                        type="range"
                        name="min"
                        id="min"
                        :min="min === null ? 0 : min"
                        :max="max === null ? 100 : max"
                        :value="min === null ? 0 : sliderMinValue"
                        :step="step"
                        @input="onInput"
                        :disabled="min === null || max === null"
                    />
                    <input
                        ref="inputMax"
                        type="range"
                        name="max"
                        id="max"
                        :min="min === null ? 0 : min"
                        :max="max === null ? 100 : max"
                        :value="max === null ? 100 : sliderMaxValue"
                        :step="step"
                        @input="onInput"
                        :disabled="min === null || max === null"
                    />
                </div>
                <div v-if="label" class="label">{{ label }}</div>
                <div v-else class="label">&nbsp;</div>
            </div>
            <input class="form-control" type="number" :step="step" v-model="sliderMaxValue" :disabled="min === null || max === null"/>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
    name: 'MinMaxSliderFilter',
    props: {
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 100,
        },
        step: {
            type: Number,
            default: 1,
        },
        minValue: {
            type: Number || null,
            default: null,
        },
        maxValue: {
            type: Number || null,
            default: null,
        },
        label: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            sliderMinValue: this.minValue ? this.minValue : this.min,
            sliderMaxValue: this.maxValue ? this.maxValue : this.max,
        };
    },
    computed: {
        sliderDifference() {
            return Math.abs(this.sliderMaxValue - this.sliderMinValue);
        },
    },
    mounted() {
        this.updateUI();
    },
    watch: {
        sliderMinValue() {
            this.updateUI();
        },
        sliderMaxValue() {
            this.updateUI();
        },
        min(newMin, oldMin) {
            if (this.sliderMinValue === oldMin) {
                this.sliderMinValue = newMin;
            }
            this.updateUI();
        },
        max(newMax, oldMax) {
            if (this.sliderMaxValue === oldMax) {
                this.sliderMaxValue = newMax;
            }
            this.updateUI();
        },
    },
    methods: {
        getPercent(value, min, max) {
            return ((value - min) / (max - min)) * 100;
        },
        setCSSProps(left, right) {
            const el = this.$refs.slider;
            if (!el) return;
            el.style.setProperty('--progressLeft', `${left}%`);
            el.style.setProperty('--progressRight', `${right}%`);
        },
        updateUI() {
            const el = this.$refs.slider;
            if (!el) return;

            if (this.min !== null) {
                this.sliderMinValue = Math.max(this.min, this.sliderMinValue);
            } else {
                this.sliderMinValue = null;
            }
            if (this.max !== null) {
                this.sliderMaxValue = Math.min(this.max, this.sliderMaxValue);
            } else {
                this.sliderMaxValue = null;
            }

            // emit uppdaterade värden
            this.emitValues();

            // beräkna procent och sätt CSS-vars
            let leftPercent = 0;
            let rightPercent = 0;
            if (this.min !== this.max && this.min !== null && this.max !== null) {
                leftPercent = this.getPercent(this.sliderMinValue, this.min, this.max);
                rightPercent = 100 - this.getPercent(this.sliderMaxValue, this.min, this.max);
            }
            this.setCSSProps(leftPercent, rightPercent);
        },
        onInput({ target }) {
            if (target.name === 'min') {
                if (Number(target.value) > this.sliderMaxValue) {
                    target.value = this.sliderMaxValue;
                } else {
                    this.sliderMinValue = parseFloat(target.value);
                }
            }
            if (target.name === 'max') {
                if (Number(target.value) < this.sliderMinValue) {
                    target.value = this.sliderMinValue;
                } else {
                    this.sliderMaxValue = parseFloat(target.value);
                }
            }
        },
        emitValues: debounce(function debounceEmitValues() {
            this.$emit('update:minValue', this.sliderMinValue === this.min ? null : this.sliderMinValue);
            this.$emit('update:maxValue', this.sliderMaxValue === this.max ? null : this.sliderMaxValue);
        }, 200),
    },
};
</script>

<style>

.custom-slider {
    --trackHeight: 0.35rem;
    --thumbRadius: 1rem;
}

/* style the input element with type "range" */
.custom-slider input[type="range"] {
    position: relative;
    appearance: none;
    background: none;
    border-radius: 999px;
    z-index: 0;
    height: 100%;
    pointer-events: none;
}

/* ::before element to replace the slider track */
.custom-slider input[type="range"]::before {
    content: "";
    display: block;
    position: absolute;
    width: var(--ProgressPercent, 100%);
    height: 100%;
    background: #2196F3;
    border-radius: 999px;
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrome and safari browsers. */
.custom-slider input[type="range"]::-webkit-slider-runnable-track {
    appearance: none;
    background: #2196f3;
    height: var(--trackHeight);
    border-radius: 999px;
}

.custom-slider input[type="range"]::-webkit-slider-thumb {
    position: relative;
    width: var(--thumbRadius);
    height: var(--thumbRadius);
    margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
    background: #2196f3;
    border: 1px solid  #2196F3;
    border-radius: 999px;
    pointer-events: all;
    appearance: none;
    z-index: 1;
}

/* `::-moz-range-track` targets the track (background) of a range slider in Mozilla Firefox. */
.custom-slider.default input[type="range"]::-moz-range-track {
    appearance: none;
    background: #2196f3;
    height: var(--trackHeight);
    border-radius: 999px;
}

.custom-slider input[type="range"]::-moz-range-thumb {
    position: relative;
    box-sizing: border-box;
    width: var(--thumbRadius);
    height: var(--thumbRadius);
    margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
    background: #2196f3;
    border: 1px solid  #2196F3;
    border-radius: 999px;
    pointer-events: all;
    appearance: none;
    z-index: 1;
}

.custom-slider.minmax {
    position: relative;
    height: var(--trackHeight);
    background: #efefef;
    border: 1px solid #ddd;
    border-radius: 999px;
    margin: 5px 0;
    --progressLeft: 0%;
    --progressRight: 0%;
}

.custom-slider .minmax-indicator {
    position: absolute;
    height: 100%;
    pointer-events: none;
    left: var(--thumbRadius);
    right: var(--thumbRadius);
}

.custom-slider .minmax-indicator::before {
    content: "";
    position: absolute;
    background: #2196F3;
    height: 100%;
    left: var(--progressLeft);
    right: var(--progressRight);
}

.custom-slider.minmax input[type="range"] {
    position: absolute;
    width: calc(100% - var(--thumbRadius));
}

.custom-slider.minmax input[type="range"][name="max"] {
    left: var(--thumbRadius);
}

.custom-slider.minmax input[type="range"]::-webkit-slider-runnable-track {
    background: none;
}

.custom-slider.minmax input[type="range"]::before {
    display: none;
}

.minmax-inputs {
    display: flex;
    flex-direction: row;
    align-items: center;
}
.minmax-inputs .slider-wrapper {
    padding: 0 5px;
    flex-grow: 1;
}
.minmax-inputs .slider-wrapper .label {
    text-align: center;
}
.minmax-inputs input {
    width: 80px;
}
</style>
